const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/liho/eCheckin/sections/Behandlungsvereinbarung.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { useCIS4m } from '@cistec/common_web/modules/CIS4m/hooks';
import { useIntl } from 'react-intl';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { createFieldPath } from 'App/components/eCheckin/utils';
import textMap from 'App/config/liho/eCheckin/translations';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CIS4mSingleCheckbox from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';
import { LabelLink } from 'App/components/eCheckin/components/fields/LabelLink';
import { useText } from '@cistec/common_web/utils/intlUtils';

const paragraphs = [
    {
        key: 'costAbsorptionTitle',
        title: textMap.costAbsorptionTitle,
        text: textMap.costAbsorptionText,
    },
    {
        key: 'involvementOfThirdParties',
        title: textMap.involvementOfThirdPartiesTitle,
        text: textMap.involvementOfThirdPartiesText,
    },
    {
        key: 'qualityControls',
        title: textMap.qualityControlsTitle,
        text: textMap.qualityControlsText,
    },
    {
        key: 'lawAndPlaceOfJurisdiction',
        title: textMap.lawAndPlaceOfJurisdictionTitle,
        text: textMap.lawAndPlaceOfJurisdictionText,
    },
];

export const Behandlungsvereinbarung = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);

    const PATH_PATIENT_INFORMATION = createFieldPath(path, 'patientInformation');
    const PATH_TREATMENT_AGREEMENT = createFieldPath(path, 'treatmentAgreement');
    const PATH_DATA_PROTECTION = createFieldPath(path, 'dataProtection');

    const { formatMessage } = useIntl();
    const eCheckinSectionRef = useCIS4m(ref);

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            {paragraphs.map(paragraph => (
                <Accordion key={paragraph.key}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content">
                        {formatMessage(paragraph.title)}
                    </AccordionSummary>
                    <AccordionDetails>{formatMessage(paragraph.text)}</AccordionDetails>
                </Accordion>
            ))}
            <CIS4mSingleCheckbox
                label={formatMessage(textMap.treatmentAgreementCheckboxLabel)}
                path={PATH_PATIENT_INFORMATION}
                required={true}
            />
            <CIS4mSingleCheckbox
                label={formatMessage(textMap.patientInformationCheckboxLabel, {
                    link: (
                        <LabelLink
                            text={textMap.patientInformationLabelLink}
                            url="https://www.lindenhofgruppe.ch/wAssets/docs/downloads/patienten-und-angehoerige/broschueren-starterpaket/deutsch/Patientenadmin_RechteVerantwortung_A5-Broschuere_de_2024_WEB.pdf"
                        />
                    ),
                })}
                path={PATH_TREATMENT_AGREEMENT}
                required={true}
            />
            <CIS4mSingleCheckbox
                label={formatMessage(textMap.dataProtectionCheckboxLabel, {
                    link: (
                        <LabelLink
                            text={textMap.dataProtectionLabelLink}
                            url="https://www.lindenhofgruppe.ch/de/rechtliches/#anchor_f195b39d_Accordion-Datenschutzerklaerung"
                        />
                    ),
                })}
                path={PATH_DATA_PROTECTION}
                required={true}
            />
        </ECheckinSection>
    );
});

Behandlungsvereinbarung.displayName = 'Behandlungsvereinbarung';

export default Behandlungsvereinbarung;
